import { cartDecision } from "@shared/campaign-rules.ts";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Card,
  Chip,
  EmptyState,
  FIELD,
  FILL_BODY,
  FILL_CARD,
  FILL_PAGE,
  FilterGroup,
  PageHeader,
  Pagination,
  ROW_SCROLL,
  STICKY_THEAD,
  usePageSize,
} from "../components/account-ui.tsx";
import { useAppDm } from "../components/legacy/app-state.tsx";
import { useShell } from "../components/legacy/Header.tsx";
import { LoadingSpinnerWithBackground } from "../components/legacy/ui.tsx";
import {
  CampaignStatusBadge,
  FinalPdfBadge,
  StatusLegend,
  StatusLegendToggle,
} from "../components/status.tsx";
import {
  CampaignOutlineIcon,
  FileCopyIcon,
  HomeWorkIcon,
  PlusIcon,
  RestoreIcon,
  TrashcanIcon,
} from "../icons/index.tsx";
import type { Campaign } from "../lib/api.ts";
import {
  type CampaignListSearch,
  filterCampaigns,
  hasActiveCampaignFilters,
  parseCampaignListSearch,
} from "../lib/campaign-list.ts";
import { facetValues, type MailFilter } from "../lib/catalog.ts";
import {
  campaignDisplayStatus,
  formatDate,
  formatNumbers,
  formatTime,
  splitName,
} from "../lib/legacy.ts";

export const Route = createFileRoute("/app/$customerId/mycampaigns/")({
  component: MyCampaigns,
  validateSearch: parseCampaignListSearch,
});

const MAIL: { id: MailFilter | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "unaddressed", label: "Unaddressed" },
  { id: "addressed", label: "Addressed" },
];

const SMALL_BTN =
  "inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors";
const OUTLINE = `${SMALL_BTN} border border-gray-300 text-gray-700 hover:bg-gray-50`;
const PRIMARY = `${SMALL_BTN} bg-primary-600 text-white shadow-sm hover:bg-primary-700`;
const ICON_BTN =
  "flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900";

function MyCampaigns() {
  const dm = useAppDm();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { approve } = search;
  const { setShowCart } = useShell();
  const { ui, set, customerId, adminMode, customer } = dm;
  const [perPage, setPerPage] = usePageSize();

  useEffect(() => {
    set({ showAddressList: false, editCampaignMode: false, renameCampaignMode: false });
    if (ui.appContext !== "myCampaigns") set({ appContext: "myCampaigns" });
  }, [set, ui.appContext]);

  useEffect(() => {
    set({
      approveArtworkMode: Boolean(approve),
      appContext: "myCampaigns",
      showAddressList: false,
    });
  }, [approve, set]);

  const open = (c: Campaign) => {
    set({
      appContext: c.source === "csv" ? "uploadAddressCSV" : "map",
      showAddressList: c.source === "csv",
      editCampaignMode: false,
      renameCampaignMode: false,
    });
    navigate({
      to: "/app/$customerId/mycampaigns/$campaignId",
      params: { customerId: c.customer_id ?? c.user_id ?? customerId, campaignId: c.id },
    });
  };

  const gated = dm.campaigns
    .filter((c) => {
      const belongsToCustomer = ui.approveArtworkMode
        ? true
        : (c.customer_id ?? c.user_id) === customerId;
      const deleted = c.status === "deleted";
      return belongsToCustomer && (ui.showDeletedCampaigns ? deleted : !deleted);
    })
    .filter((c) => (ui.approveArtworkMode ? c.approval_status === "approved_by_creator" : true));
  const rows = filterCampaigns(gated, dm.products, search);
  const listKey = JSON.stringify([search, perPage, ui.showDeletedCampaigns]);
  const [paged, setPaged] = useState({ key: listKey, n: 0 });
  const page = Math.min(
    paged.key === listKey ? paged.n : 0,
    Math.max(0, Math.ceil(rows.length / perPage) - 1),
  );
  const shown = rows.slice(page * perPage, (page + 1) * perPage);
  const facets = facetValues(
    gated.flatMap((c) => {
      const product = dm.products.find((p) => p.id === c.product_id);
      return product ? [product] : [];
    }),
  );
  const filtering = hasActiveCampaignFilters(search);

  const apply = (patch: Partial<CampaignListSearch>, replace = false) => {
    const next: CampaignListSearch = { ...search, ...patch };
    for (const key of Object.keys(next) as (keyof CampaignListSearch)[]) {
      if (next[key] === undefined) delete next[key];
    }
    navigate({
      to: "/app/$customerId/mycampaigns",
      params: { customerId },
      search: next,
      replace,
    });
  };

  const choose = <K extends "size" | "pages">(key: K, value: CampaignListSearch[K]) =>
    apply({ [key]: search[key] === value ? undefined : value });

  const reset = () =>
    navigate({
      to: "/app/$customerId/mycampaigns",
      params: { customerId },
      search: approve ? { approve: true } : {},
    });

  const name = customer ? splitName(customer.name) : null;
  const title = adminMode
    ? ui.approveArtworkMode
      ? "Campaign approval"
      : `Campaigns for ${name?.firstName ?? ""} ${name?.lastName ?? ""}`.trim()
    : "My campaigns";
  const subtitle = adminMode
    ? ui.approveArtworkMode
      ? "Campaigns the customer has approved and that are waiting on eConnect."
      : customer?.organisation
    : ui.showDeletedCampaigns
      ? "Deleted campaigns can be restored."
      : "Open a campaign to keep working on it.";
  const toggleLabels = () => set({ showStatusPills: !ui.showStatusPills });

  return (
    <div className={FILL_PAGE}>
      {dm.loading && <LoadingSpinnerWithBackground />}
      <div className="shrink-0">
        <PageHeader
          title={title}
          subtitle={subtitle}
          action={
            <div className="flex flex-wrap items-center gap-2">
              {adminMode && (
                <button
                  type="button"
                  className={OUTLINE}
                  onClick={() =>
                    navigate({ to: "/app/$customerId/myorders", params: { customerId } })
                  }
                >
                  Customer Orders
                </button>
              )}
              <button
                type="button"
                aria-pressed={ui.showDeletedCampaigns}
                className={`${OUTLINE} ${ui.showDeletedCampaigns ? "border-amber-300 bg-amber-50 text-amber-800" : ""}`}
                onClick={() => set({ showDeletedCampaigns: !ui.showDeletedCampaigns })}
              >
                {ui.showDeletedCampaigns ? "Hide Deleted" : "Show Deleted"}
              </button>
              <StatusLegendToggle
                show={ui.showLegend}
                onToggle={() => set({ showLegend: !ui.showLegend })}
              />
              <button type="button" className={PRIMARY} onClick={() => dm.createCampaign()}>
                <PlusIcon className="h-5 w-5" />
                New Campaign
              </button>
            </div>
          }
        />
        <StatusLegend
          show={ui.showLegend}
          adminMode={adminMode}
          todaysDate={formatDate(new Date().toISOString())}
          showLabels={ui.showStatusPills}
          onToggleLabels={toggleLabels}
        />

        <Card className="mb-6">
          <div className="flex flex-col gap-4">
            <input
              type="search"
              name="q"
              value={search.q ?? ""}
              placeholder="Search campaign name"
              aria-label="Search campaign name"
              className={FIELD}
              onChange={(e) => apply({ q: e.target.value || undefined }, true)}
            />
            <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
              <FilterGroup label="Mail type">
                <div className="inline-flex rounded-xl bg-gray-100 p-1">
                  {MAIL.map((m) => {
                    const active = (search.mail ?? "all") === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        aria-pressed={active}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                          active
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                        onClick={() => apply({ mail: m.id === "all" ? undefined : m.id })}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </FilterGroup>

              {facets.sizes.length > 0 && (
                <FilterGroup label="Size">
                  {facets.sizes.map((size) => (
                    <Chip
                      key={size}
                      active={search.size === size}
                      onClick={() => choose("size", size)}
                    >
                      {size}
                    </Chip>
                  ))}
                </FilterGroup>
              )}

              {facets.pages.length > 1 && (
                <FilterGroup label="Pages">
                  {facets.pages.map((pages) => (
                    <Chip
                      key={pages}
                      active={search.pages === pages}
                      onClick={() => choose("pages", pages)}
                    >
                      {`${pages}pp`}
                    </Chip>
                  ))}
                </FilterGroup>
              )}

              {filtering && (
                <button
                  type="button"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                  onClick={reset}
                >
                  Reset
                </button>
              )}

              <label className="ml-auto flex items-center gap-2 text-sm text-gray-600">
                Sort
                <select
                  className="rounded-lg border-gray-300 py-1.5 pl-3 pr-8 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                  value={search.sort ?? "newest"}
                  onChange={(e) =>
                    apply({
                      sort:
                        e.target.value === "newest"
                          ? undefined
                          : (e.target.value as CampaignListSearch["sort"]),
                    })
                  }
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="name">Name A–Z</option>
                  <option value="name-desc">Name Z–A</option>
                  <option value="updated">Recently modified</option>
                  <option value="addresses">Most addresses</option>
                </select>
              </label>
            </div>
          </div>
        </Card>
      </div>

      <Card className={FILL_CARD} bodyClass={FILL_BODY}>
        {rows.length === 0 && dm.loading ? (
          <div className="h-40" />
        ) : rows.length === 0 && gated.length > 0 ? (
          <EmptyState
            Icon={CampaignOutlineIcon}
            title="No campaigns match"
            message="Try a different name or fewer filters."
            action={
              <button type="button" className={PRIMARY} onClick={reset}>
                Clear filters
              </button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            Icon={CampaignOutlineIcon}
            title={
              adminMode
                ? "This customer has not created any campaigns"
                : "Create your first campaign"
            }
            message={
              adminMode
                ? "Click New Campaign to create their first campaign."
                : "Give it a name, then click save to get started."
            }
            action={
              <button type="button" className={PRIMARY} onClick={() => dm.createCampaign()}>
                New Campaign
              </button>
            }
          />
        ) : (
          <>
            <div className={ROW_SCROLL}>
              <table className="w-full text-left text-sm">
                <thead className={STICKY_THEAD}>
                  <tr>
                    <th className="px-5 py-3 font-semibold">Campaign</th>
                    <th className="px-5 py-3 font-semibold">Addresses</th>
                    <th className="hidden px-5 py-3 font-semibold lg:table-cell">Created</th>
                    <th className="hidden px-5 py-3 font-semibold xl:table-cell">Modified</th>
                    <th className="px-5 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shown.map((c) => {
                    const shown = campaignDisplayStatus(c, adminMode);
                    const cart = cartDecision(c);
                    return (
                      <tr
                        key={c.id}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => open(c)}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <CampaignStatusBadge
                              status={shown.status}
                              label={shown.label}
                              showLabel={ui.showStatusPills}
                              onToggle={toggleLabels}
                            />
                            <FinalPdfBadge
                              status={c.final_pdf_status}
                              updatedAt={c.final_pdf_updated_at}
                            />
                            <span className="min-w-0">
                              <span className="block truncate font-medium text-gray-900">
                                {c.name === "" ? "Untitled Campaign" : c.name}
                              </span>
                              {ui.showIDs && (
                                <span className="block font-mono text-[10px] text-rose-700">
                                  {c.id}
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-gray-600">
                          <span className="inline-flex items-center gap-1.5 tabular-nums">
                            <HomeWorkIcon className="h-4 w-4 fill-current text-gray-400" />
                            {formatNumbers(c.address_count ?? 0) || "0"}
                          </span>
                        </td>
                        <td className="hidden whitespace-nowrap px-5 py-4 text-gray-600 lg:table-cell">
                          {formatDate(c.created_at)}
                          <span className="ml-2 text-xs text-gray-400">
                            {formatTime(c.created_at)}
                          </span>
                        </td>
                        <td className="hidden whitespace-nowrap px-5 py-4 text-gray-600 xl:table-cell">
                          {formatDate(c.updated_at)}
                          <span className="ml-2 text-xs text-gray-400">
                            {formatTime(c.updated_at)}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          {ui.approveArtworkMode ? (
                            <div className="flex justify-end">
                              <button
                                type="button"
                                className={PRIMARY}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  set({ appContext: "approveArtwork" });
                                  navigate({
                                    to: "/app/$customerId/mycampaigns/$campaignId",
                                    params: {
                                      customerId: c.customer_id ?? c.user_id ?? customerId,
                                      campaignId: c.id,
                                    },
                                    search: { panel: "artwork" },
                                  });
                                }}
                              >
                                Approve…
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                className={ICON_BTN}
                                aria-label="Duplicate campaign"
                                title="Duplicate"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dm.duplicateCampaign(c.id);
                                }}
                              >
                                <FileCopyIcon className="h-4 w-4 fill-current" />
                              </button>
                              <button
                                type="button"
                                className={OUTLINE}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  open(c);
                                }}
                              >
                                Open
                              </button>
                              <button
                                type="button"
                                className={`${PRIMARY} whitespace-nowrap aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-primary-600`}
                                aria-disabled={cart.kind === "blocked" || undefined}
                                title={cart.kind === "blocked" ? cart.message : undefined}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (cart.kind === "in_cart") setShowCart(true);
                                  else if (cart.kind === "ready") void dm.addToCart(c.id);
                                  else dm.notice("Add to cart", cart.message);
                                }}
                              >
                                {cart.kind === "in_cart" ? "My Cart" : "Add to Cart"}
                              </button>
                              {c.status === "deleted" ? (
                                <button
                                  type="button"
                                  className={`${ICON_BTN} text-emerald-600 hover:text-emerald-700`}
                                  aria-label="Restore campaign"
                                  title="Restore"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dm.restoreCampaign(c.id);
                                  }}
                                >
                                  <RestoreIcon className="h-5 w-5 fill-current" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className={`${ICON_BTN} hover:text-rose-600`}
                                  aria-label="Delete campaign"
                                  title="Delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dm.deleteCampaign(c.id);
                                  }}
                                >
                                  <TrashcanIcon className="h-4 w-4 fill-current" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={perPage}
              total={rows.length}
              onPage={(n) => setPaged({ key: listKey, n })}
              onPageSize={setPerPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}
